<script context="module" lang="ts">
  export enum ContentSlotPlacement {
    None = "NONE",
    Slot1 = "SLOT1",
    Slot2 = "SLOT2",
    Slot3 = "SLOT3",
    Slot4 = "SLOT4",
    Slot5 = "SLOT5",
  }
</script>

<script lang="ts">
  import type { FliteImage, FliteVideo, Link } from "$lib/gql/genCms/codegen";
  import { Alignment } from "$lib/gql/genCms/codegen";
  import { Position, MediaType } from "$lib/gql/genCms/codegen";
  import Button from "$lib/components/Button/Button.svelte";
  import Container from "$lib/components/Container/Container.svelte";
  import Grid from "$lib/components/Grid/Grid.svelte";
  import Column from "$lib/components/Column/Column.svelte";
  import Video from "$lib/components/Video/Video.svelte";
  import Typography from "$lib/components/Typography/Typography.svelte";
  import inView from "$lib/utils/inView";
  import HeightSkeleton from "$lib/components/HeightSkeleton/HeightSkeleton.svelte";
  import type { ImageConfig } from "./ShowcaseFeature.image-config";
  import { defaultImgConfig } from "./ShowcaseFeature.image-config";
  import { onMount } from "svelte";
  import ShowcaseFeatureLink from "$lib/components/ShowcaseFeatureLink/ShowcaseFeatureLink.svelte";
  import type { Page } from "@sveltejs/kit";
  import { page as pageStore } from "$app/stores";
  import type { Readable } from "svelte/store";
  import { processInlineLinks } from "$lib/utils/url";
  // import Counter from "$lib/components/Counter/Counter.svelte";
  import Divider from "$lib/components/Divider/Divider.svelte";
  import Picture from "../Picture/Picture.svelte";

  let isMobile = false;
  let page: Readable<Page> = pageStore;
  export let mediaType: MediaType = MediaType.Image;
  export let isFirstBlock: boolean = false;
  export let image: FliteImage = {};
  export let imageSm: FliteImage = {};
  export let imageAlt: string = "";
  export let title: string;
  export let subtitle: string;
  export let body: string;
  export let ctaOneText: string;
  export let ctaOneLink: string;
  export let ctaTwoText: string;
  export let ctaTwoLink: string;
  export let ctaOneBgColour: string;
  export let ctaTwoBgColour: string;
  export let ctaOneTextLight = false;
  export let ctaTwoTextLight = false;
  export let invert = false;
  export let position: Position;
  export let alignment: Alignment;
  export let headingElement = "h1";
  export let video: FliteVideo = {};
  export let videoSm: FliteVideo = {};
  export let imageConfig: ImageConfig = defaultImgConfig;
  export let blockCtaText: string = "";
  export let blockCtaLink: string = "";
  export let contentSlotPlacement: ContentSlotPlacement =
    ContentSlotPlacement.None;
  export let borderBottom = true;
  // export let animate = false;
  export let rectangleDivider = false;
  export let muted = true;
  export let autoPlay = true;
  export let controls = false;

  export let headingVariant = isMobile ? "h1" : "display1";
  $: headingVariant = isMobile ? "h1" : "display1";
  $: isMobile = viewportWidth < mdWidth;
  $: isHovered = false;

  $: isContentSlot = contentSlotPlacement !== ContentSlotPlacement.None;

  /**
   * To select the amount of columns that the Media Type takes in the LG breakpoint
   *
   * `12` | `10`
   * @defaultValue `12`
   */
  export let mediaColumns = 12;
  /**
   * To select the display direction for the mobile content
   *
   * `column` | `column-reverse` | `absolute`
   * @defaultValue `column`
   */
  export let displayDirection = "column";
  /**
   * To select the positioning of the text in the Y axis
   *
   * `centred` | `fixed`
   * @defaultValue `centred`
   */
  export let contentPosition = "centred";

  export let fluid = false;

  let sources = [];
  $: if (video || videoSm) {
    sources = [
      {
        poster: null,
        src: videoSm?.uri,
        minWidth: 0,
        maxWidth: 1279,
        type: "video/mp4",
        invert: false,
      },
      {
        poster: null,
        src: video?.uri,
        minWidth: 1280,
        maxWidth: 10000,
        type: "video/mp4",
        invert: false,
      },
    ];
  }

  let textPosition: string;

  $: if (position) {
    if (position === Position.Right) {
      textPosition = "right";
    } else if (position === Position.Center) {
      textPosition = "center";
    } else {
      textPosition = "left";
    }
  }

  let isInView = false;
  export let videoShouldPlay = false;

  let viewportWidth: number = 0;
  let mdWidth = 768;

  let contentContainerLink: string = "";
  let mediaContainerLink: string = "";

  $: if (blockCtaLink && !ctaOneLink && !ctaTwoLink) {
    contentContainerLink = blockCtaLink;
    mediaContainerLink = blockCtaLink;
  } else if (blockCtaLink && ctaOneLink && !ctaTwoLink) {
    contentContainerLink = ctaOneLink;
    mediaContainerLink = ctaOneLink;
  } else if (blockCtaLink && ctaOneLink && ctaTwoLink) {
    contentContainerLink = null;
    mediaContainerLink = blockCtaLink;
  }

  onMount(() => {
    const bodyStyle = getComputedStyle(document.body);
    mdWidth = Number(bodyStyle.getPropertyValue("--md").replace(/\D/g, ""));
  });

  let contentAlignment: string = "center";
  $: if (alignment) {
    if (alignment === Alignment.Top) {
      contentAlignment = "top";
    } else if (alignment === Alignment.Bottom) {
      contentAlignment = "bottom";
    } else {
      contentAlignment = "center";
    }
  }
</script>

<style lang="scss">
  .showcase-feature {
    position: relative;

    &.borderBottom {
      border-bottom: 1px solid $background;

      @include retina {
        border-bottom: 0.5px solid $background;
      }
    }

    :global(a) {
      text-decoration: none;
    }

    :global(picture),
    :global(video-js) {
      display: block;
      overflow: hidden;
    }

    :global(a.sfc-link video-js) {
      pointer-events: none;
    }

    :global(a.sfc-link .media-container img),
    :global(a.sfc-link .media-container video) {
      transition: $transition;
    }

    :global(a.sfc-link:hover .media-container img),
    :global(a.sfc-link:hover .media-container video),
    :global(a.sfc-link.hoverState .media-container img),
    :global(a.sfc-link.hoverState .media-container video) {
      transform: scale(1.08);
      transform-origin: center;
    }
  }

  .isFirstBlock {
    margin-top: 64px;
  }

  .container {
    opacity: 0;

    &.fade-in {
      opacity: 1;
      transition: all $animDuration;
    }

    &--column {
      display: flex;
      flex-direction: column;
    }

    &--column-reverse {
      display: flex;
      flex-direction: column-reverse;
    }
  }
  .container {
    display: grid;
    grid-template-areas: "showcase";

    .media-container,
    .content-wrapper {
      grid-area: showcase;
    }
  }

  .content-wrapper {
    pointer-events: none;

    &--absolute {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      z-index: 50;

      > :global(div) {
        width: 100%;
      }
    }
  }
  .content-wrapper {
    z-index: 50;
    display: flex;
    align-items: center;
    pointer-events: none;

    .wrapper {
      pointer-events: auto;
    }
  }

  .wrapper {
    padding: 32px 0;
    margin-bottom: 0;
    grid-column: span 4;

    &--center {
      display: flex;
      flex-direction: column;
      justify-content: center;
      text-align: center;

      .ctas {
        justify-content: center;
      }
    }

    .ctas {
      margin-left: -8px;
    }
  }

  .content-wrapper {
    &--align-top {
      align-items: flex-start;
      top: 61px;
      @include breakpoint(md) {
        top: 113px;
      }
    }
    &--align-bottom {
      align-items: flex-end;
    }
    &--align-center {
      align-items: center;
    }

    > :global(div) {
      width: 100%;
    }
  }
  .counter-container {
    pointer-events: none;
    bottom: 0px;
    right: 0px;
    position: absolute;
  }

  .invert {
    background-color: $inverse-surface;
  }

  .label {
    margin-bottom: 15px;
  }
  .title + .description {
    margin-top: 13px;
  }

  .title + .ctas {
    margin-top: 24px;
  }

  .description + .ctas {
    margin-top: 34px;
  }

  .ctas {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: 16px;
    pointer-events: auto;
  }
  .cta {
    margin: 0 8px;
    &:last-child {
      margin-bottom: 0;
    }
  }
  .media-container {
    position: relative;
    &--video {
      overflow: hidden;
      aspect-ratio: 4/5;

      @include breakpoint(md) {
        aspect-ratio: 192/175;
      }
      @include breakpoint(lg) {
        aspect-ratio: 32 / 15;
      }
    }
    &--image {
      overflow: hidden;
      @include breakpoint(md) {
        aspect-ratio: 192/175;
      }
      @include breakpoint(lg) {
        aspect-ratio: 32 / 15;
      }
    }

    &--full-height {
      height: 100%;
    }
  }

  .html :global(p),
  .html :global(span) {
    padding: 0;
    margin: 0;
    color: $on-surface;
  }

  .html--invert :global(*) {
    // Set the colour explicitly on ALL elements.
    // We can't guarantee the nesting structure when pasting from various sources,
    // So we need a nuclear option.
    color: $inverse-on-surface !important;
  }

  :global(.destination-feature > .showcase-feature a.sfc-link video-js),
  :global(.shop-the-look > .showcase-feauture a.sfc-link video-js) {
    z-index: auto;
  }

  @include breakpoint(md) {
    .isFirstBlock {
      margin-top: 0;
    }

    .invert {
      background-color: transparent;
    }

    .wrapper {
      position: relative;

      &--left {
        grid-column: auto / span 4;
      }

      &--center {
        grid-column: 3 / span 4;
      }

      &--right {
        grid-column: 5 / span 4;
      }
    }
    .wrapper-inner {
      position: relative;
      opacity: 0;
    }

    .isInView {
      &--fixed {
        @include breakpoint(md) {
          top: 20%;
        }

        @include breakpoint(lg) {
          top: 50%;
        }
        @include breakpoint(xl) {
          top: 10%;
        }
      }
      &--centred {
        top: 50%;
      }
      opacity: 1;
      transform: translateY(-50%);
      transition: $transition;
    }
  }

  @include breakpoint(lg) {
    .wrapper {
      margin-bottom: 64px;
      padding: 64px 0;

      .ctas {
        margin-left: -12px;
      }

      .cta {
        margin-left: 12px;
        margin-right: 12px;
      }
      &--left {
        grid-column: auto / span 6;
      }
      &--center {
        grid-column: 4 / span 6;
      }

      &--right {
        grid-column: 8 / span 6;
      }
    }
  }

  .showcase-feature {
    &--cs {
      height: 100%;
      width: 100%;
      object-fit: contain;
      object-position: center;

      .container,
      .media-container {
        height: 100%;
      }

      &__one {
        .content-wrapper {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          z-index: 50;
          &--align-top {
            align-items: flex-start;
          }
          &--align-bottom {
            align-items: flex-end;
          }
          &--align-center {
            align-items: center;
          }

          & :global(> div) {
            width: 100%;
          }
        }

        .wrapper {
          display: flex;
          flex-direction: column;
          justify-content: center;
          text-align: center;

          .ctas {
            justify-content: center;
          }

          @include breakpoint(md) {
            &--right,
            &--left,
            &--center {
              grid-column: 2 / span 6;
            }
          }
          @include breakpoint(lg) {
            &--right,
            &--left,
            &--center {
              grid-column: 3 / span 8;
            }
            margin: 0;
          }
        }
      }

      &__two {
        .content-wrapper {
          align-items: center;
          height: 100%;
          &--align-top {
            align-items: flex-start;
          }
          &--align-bottom {
            align-items: flex-end;
          }
          &--align-center {
            align-items: center;
          }

          & :global(> div) {
            width: 100%;
          }
        }
        :global(div.container > a) {
          grid-area: showcase;
          z-index: 1;
          height: 100%;
        }

        .wrapper {
          display: flex;
          flex-direction: column;
          justify-content: center;
          text-align: center;

          .ctas {
            justify-content: center;
          }
          @include breakpoint(md) {
            &--right,
            &--left,
            &--center {
              grid-column: 3 / span 4;
            }
          }
          @include breakpoint(lg) {
            &--right,
            &--left,
            &--center {
              grid-column: 2 / span 10;
            }
            margin: 0;
          }
        }
      }
    }
  }
</style>

<div
  class="showcase-feature {isContentSlot
    ? 'showcase-feature--cs'
    : ''} {contentSlotPlacement === ContentSlotPlacement.Slot1
    ? 'showcase-feature--cs__one'
    : contentSlotPlacement === ContentSlotPlacement.Slot2
      ? 'showcase-feature--cs__two'
      : ''}"
  data-cs-override-id="showcase-feature"
  class:invert
  class:isFirstBlock
  class:borderBottom
  use:inView={{ threshold: 0.1 }}
  on:intersect={({ detail }) => {
    if (detail) {
      isInView = true;

      if (autoPlay) videoShouldPlay = true;
    } else {
      videoShouldPlay = false;
    }
  }}
>
  <div
    class="container container--{displayDirection} {isInView ? 'fade-in' : ''}"
  >
    <ShowcaseFeatureLink
      block
      label={blockCtaText}
      {isHovered}
      link={mediaContainerLink}
      fullHeight={isContentSlot}
    >
      <div class="media-container">
        <Grid smCols={4} mdCols={8} lgCols={12}>
          <Column smSpan={4} mdSpan={8} lgSpan={mediaColumns}>
            <div
              class={!isContentSlot && !fluid && mediaType === MediaType.Video
                ? "media-container--video"
                : !isContentSlot && !fluid && mediaType === MediaType.Image
                  ? "media-container--image"
                  : "media-container--full-height"}
            >
              {#if mediaType === MediaType.Video && video && video.uri && video.uri !== ""}
                {#each sources as source}
                  {#if viewportWidth && viewportWidth >= source.minWidth && viewportWidth < source.maxWidth}
                    {#key $page?.url?.pathname}
                      <Video
                        poster={source.poster}
                        sources={[
                          {
                            src: source.src,
                            type: source.type ? source.type : "video/mp4",
                          },
                        ]}
                        autoplay={autoPlay}
                        {controls}
                        {muted}
                        loop={true}
                        fluid={isContentSlot || fluid ? true : false}
                        fill={true}
                        preload="auto"
                        {videoShouldPlay}
                      />
                      <!-- <div class="counter-wrapper">
                        {#if animate}
                          <div class="counter-container">
                            <Counter {invert} />
                          </div>
                        {/if}
                      </div> -->
                    {/key}
                  {/if}
                {/each}
              {:else if mediaType === MediaType.Image && image && image.uri && image.uri !== ""}
                <!-- <div class="counter-wrapper"> -->
                <Picture
                  mdImage={image.uri}
                  smImage={imageSm.uri}
                  {imageConfig}
                  altText={imageAlt}
                  cover={isContentSlot}
                  fitOrFill="fill"
                />

                <!-- {#if animate}
                    <div class="counter-container">
                      <Counter {invert} />
                    </div>
                  {/if} -->
                <!-- </div> -->
              {:else}
                <HeightSkeleton
                  smSize="0%"
                  mdSize="91.16%"
                  lgSize="46.9%"
                  xlSize="46.87%"
                />
              {/if}
            </div>
          </Column>
        </Grid>
      </div>
    </ShowcaseFeatureLink>

    {#if title || subtitle || body || (ctaOneLink && ctaOneText) || (ctaTwoLink && ctaTwoText) || $$slots.cards}
      <ShowcaseFeatureLink
        block
        label={blockCtaText}
        {isHovered}
        link={$$slots.cards && viewportWidth >= 768
          ? null
          : contentContainerLink}
      >
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div
          class="content-wrapper content-wrapper--{displayDirection} content-wrapper--align-{contentAlignment}"
          style={`${contentPosition === "fixed" && "top: -10%"};`}
          on:mouseenter={() =>
            !$$slots.cards &&
            contentContainerLink &&
            viewportWidth >= 768 &&
            (isHovered = true)}
          on:mouseleave={() =>
            !$$slots.cards &&
            contentContainerLink &&
            viewportWidth >= 768 &&
            (isHovered = false)}
        >
          <Container>
            <Grid smCols={4} mdCols={8} lgCols={12}>
              <div class="wrapper wrapper--{textPosition}">
                <div
                  class="wrapper-inner isInView--{contentPosition}"
                  class:isInView
                >
                  {#if subtitle}
                    <div class="label">
                      <Typography
                        component="span"
                        variant="button1"
                        theme={invert ? "inverse-on-surface" : "on-surface"}
                        >{subtitle}</Typography
                      >
                    </div>
                  {/if}

                  {#if title}
                    <div class="title">
                      <Typography
                        component={headingElement}
                        variant={headingVariant}
                        theme={invert ? "inverse-on-surface" : "on-surface"}
                        >{title}</Typography
                      >
                    </div>
                  {/if}
                  {#if rectangleDivider}
                    <Divider {invert} {textPosition} />
                  {/if}
                  {#if body}
                    <div class="description">
                      <Typography
                        component="span"
                        variant="body1"
                        theme={invert ? "inverse-on-surface" : "on-surface"}
                      >
                        <div class={`html ${invert && "html--invert"}`}>
                          {@html processInlineLinks(body)}
                        </div>
                      </Typography>
                    </div>
                  {/if}

                  {#if (ctaOneLink && ctaOneText) || (ctaTwoLink && ctaTwoText)}
                    <div class="ctas">
                      {#if ctaOneLink && ctaOneText}
                        <div class="cta">
                          <Button
                            primary
                            href={ctaOneLink}
                            label={ctaOneText}
                            primaryInvert={invert || ctaOneTextLight}
                            theme={(invert && !ctaOneTextLight) ||
                            (!invert && ctaOneTextLight)
                              ? "on-surface"
                              : "inverse-on-surface"}
                            hoverTheme={!ctaOneTextLight
                              ? "on-surface"
                              : "inverse-on-surface"}
                            backgroundColor={ctaOneBgColour
                              ? ctaOneBgColour
                              : invert && !ctaOneBgColour
                                ? "on-surface"
                                : "inverse-on-surface"}
                            hoverBgColor="transparent"
                          />
                        </div>
                      {/if}

                      {#if ctaTwoLink && ctaTwoText}
                        <div class="cta">
                          <Button
                            primary
                            href={ctaTwoLink}
                            label={ctaTwoText}
                            primaryInvert={invert || ctaTwoTextLight}
                            theme={(invert && !ctaTwoTextLight) ||
                            (!invert && ctaTwoTextLight)
                              ? "on-surface"
                              : "inverse-on-surface"}
                            hoverTheme={!ctaTwoTextLight
                              ? "on-surface"
                              : "inverse-on-surface"}
                            backgroundColor={ctaTwoBgColour
                              ? ctaTwoBgColour
                              : invert && !ctaTwoBgColour
                                ? "on-surface"
                                : "inverse-on-surface"}
                            hoverBgColor="transparent"
                          />
                        </div>
                      {/if}
                    </div>
                  {/if}
                </div>
              </div>

              <slot name="cards" />
            </Grid>
          </Container>
        </div>
      </ShowcaseFeatureLink>
    {/if}
  </div>
</div>

<svelte:window bind:innerWidth={viewportWidth} />
